"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Users, Laptop, Compass, ShieldCheck, Sparkles, Globe, CircleCheck as CheckCircle2, Layers, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface EnvironmentCardData {
  id: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    altVi: string;
    altEn: string;
  };
  titleVi: string;
  titleEn: string;
  categoryVi: string;
  categoryEn: string;
  descVi: string;
  descEn: string;
  featuresVi: string[];
  featuresEn: string[];
  icon: React.ElementType;
}
const ENVIRONMENT_CARDS: EnvironmentCardData[] = [{
  id: "high-tech-lab",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/facilities_lab_1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/facilities_lab_1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/facilities_lab_1.png-wvc-sizes",
    altVi: "Phòng lab Robotics hiện đại với bộ kit LEGO Education",
    altEn: "Modern Robotics Lab with LEGO Education Kits"
  },
  titleVi: "Phòng Lab Robotics Chuẩn Quốc Tế",
  titleEn: "International Standard Robotics Lab",
  categoryVi: "Thiết Bị Cốt Lõi",
  categoryEn: "Core Equipment",
  descVi: "Không gian thực hành trang bị 100% bộ giáo cụ LEGO SPIKE, Mindstorms và phần mềm lập trình chuyên dụng cho từng lứa tuổi.",
  descEn: "Hands-on workspace equipped 100% with LEGO SPIKE, Mindstorms sets, and age-tailored dedicated programming software.",
  featuresVi: ["Bộ kít LEGO Education chính hãng", "Máy tính cấu hình cao chuyên dùng lập trình", "Bàn thảm thi đấu chuẩn WRO"],
  featuresEn: ["Authentic LEGO Education kits", "High-spec coding workstations", "WRO competition-grade mats & tables"],
  icon: Cpu
}, {
  id: "interactive-group-learning",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_teacher_interaction.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_teacher_interaction.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/student_teacher_interaction.png-wvc-sizes",
    altVi: "Học sinh làm việc nhóm cùng sự hướng dẫn của giáo viên",
    altEn: "Students engaging in teamwork with teacher guidance"
  },
  titleVi: "Góc Tương Tác Nhóm Nhỏ",
  titleEn: "Small-Group Interactive Stations",
  categoryVi: "Môi Trường Học Tập",
  categoryEn: "Learning Space",
  descVi: "Mô hình lớp học giới hạn từ 6-8 học sinh/lớp giúp giáo viên đồng hành sâu sát và khuyến khích thảo luận, phản biện.",
  descEn: "Small class sizes (6-8 students/class) ensuring direct teacher mentorship and encouraging collaborative discussion.",
  featuresVi: ["Tỷ lệ giáo viên/học sinh tối ưu (1:6)", "Bàn học mô-đun linh hoạt tháo lắp", "Không gian thuyết trình dự án"],
  featuresEn: ["Optimal teacher-to-student ratio (1:6)", "Flexible modular workstation desks", "Dedicated project presentation areas"],
  icon: Users
}, {
  id: "coding-hardware-station",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/programming_hardware_station.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/programming_hardware_station.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/programming_hardware_station.png-wvc-sizes",
    altVi: "Trạm phần cứng lập trình và vi mạch điện tử",
    altEn: "Programming hardware station and electronic microcircuits"
  },
  titleVi: "Trạm Lập Trình & Vi Mạch",
  titleEn: "Coding & Microcircuit Workstations",
  categoryVi: "Công Nghệ Nâng Cao",
  categoryEn: "Advanced Tech",
  descVi: "Trang bị phần cứng mở rộng bao gồm Micro:bit, Arduino và linh kiện cảm biến cho các học viên học lập trình chuyên sâu.",
  descEn: "Equipped with expansion hardware including Micro:bit, Arduino, and sensor modules for advanced engineering tracks.",
  featuresVi: ["Mạch lập trình Arduino & Micro:bit", "Hệ thống cảm biến đo đạc thực tế", "Công cụ kiểm thử thuật toán"],
  featuresEn: ["Arduino & Micro:bit programming boards", "Real-world measurement sensor modules", "Algorithm debugging tools"],
  icon: Laptop
}, {
  id: "tailored-center-facilities",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/facilities_center_tailored.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/facilities_center_tailored.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/facilities_center_tailored.png-wvc-sizes",
    altVi: "Không gian trung tâm được tinh chỉnh phù hợp với khuôn viên từng cơ sở",
    altEn: "Center space tailored specifically to each campus location"
  },
  titleVi: "Cơ Sở Vật Chất Được Tinh Chỉnh Theo Trung Tâm",
  titleEn: "Location-Tailored Center Facilities",
  categoryVi: "Thiết Kế Cơ Sở",
  categoryEn: "Campus Design",
  descVi: "Mỗi trung tâm ROBOTO được thiết kế tối ưu theo đặc thù không gian cơ sở nhằm mang lại trải nghiệm tiện nghi và an toàn nhất.",
  descEn: "Each ROBOTO location is custom-layouted to suit its campus footprint, providing optimal comfort, accessibility, and safety.",
  featuresVi: ["Bố trí không gian linh hoạt theo cơ sở", "Khu vực chờ phụ huynh thoải mái", "Trang bị an toàn đạt tiêu chuẩn"],
  featuresEn: ["Flexible spatial layout per location", "Comfortable parent waiting lounges", "Safety compliant environment"],
  icon: Compass
}];
export default function LearningEnvironment() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="learningenvironment" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Blueprint Grid Technical Pattern Overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.035] dark:opacity-[0.07] bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Top Controls & Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6 pb-6 border-b border-border">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary" className="gap-1 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                <Sparkles className="size-3 text-primary" />
                {lang === "vi" ? "TRẢI NGHIỆM THỰC TẾ" : "REAL EXPERIENCE"}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {lang === "vi" ? "HỆ THỐNG PHÒNG LAB" : "LAB SYSTEM"}
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
              {lang === "vi" ? "KHÔNG GIAN HỌC TẬP" : "The Learning Environment"}
            </h2>

            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {lang === "vi" ? "Không gian học tập tại ROBOTO được đầu tư trang thiết bị Robotics hiện đại, thiết kế kích thích tư duy sáng tạo và khả năng làm việc nhóm. Cơ sở vật chất được tinh chỉnh phù hợp với quy mô và đặc thụ từng trung tâm." : "The learning environment at ROBOTO features modern Robotics equipment designed to inspire creative thinking and collaborative teamwork. Facilities are tailored specifically to suit each center location."}
            </p>
          </div>

          {/* Bilingual Switcher */}
          <div className="flex items-center gap-2 self-start md:self-end bg-muted/60 p-1.5 rounded-lg border border-border">
            <Globe className="size-4 text-muted-foreground ml-2" />
            <span className="text-xs font-medium text-muted-foreground mr-1">Language:</span>
            <Button variant="ghost" size="sm" onClick={() => setLang("vi")} className={`h-auto px-3 py-1 rounded text-xs font-semibold transition-colors ${lang === "vi" ? "bg-primary text-primary-foreground shadow-xs" : "text-foreground hover:bg-background/80"}`}>
              Tiếng Việt
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setLang("en")} className={`h-auto px-3 py-1 rounded text-xs font-semibold transition-colors ${lang === "en" ? "bg-primary text-primary-foreground shadow-xs" : "text-foreground hover:bg-background/80"}`}>
              English
            </Button>
          </div>
        </div>

        {/* Feature Grid with Framer Motion */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ENVIRONMENT_CARDS.map((card, i) => {
          const IconComponent = card.icon;
          const altText = lang === "vi" ? card.image.altVi : card.image.altEn;
          const title = lang === "vi" ? card.titleVi : card.titleEn;
          const category = lang === "vi" ? card.categoryVi : card.categoryEn;
          const desc = lang === "vi" ? card.descVi : card.descEn;
          const features = lang === "vi" ? card.featuresVi : card.featuresEn;
          return <motion.div key={card.id} variants={itemVariants} whileHover={{
            y: -4
          }} transition={{
            duration: 0.2
          }} data-index={i} className="h-full">
                <Card className="h-full flex flex-col overflow-hidden border-border bg-card shadow-sm hover:shadow-md transition-all duration-300 group">
                  {/* Card Image Block */}
                  <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                    <img src={card.image.src} data-wvc-srcset={card.image.srcset} data-wvc-sizes={card.image.sizes} alt={altText} loading="lazy" className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />
                    
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="bg-background/90 text-foreground backdrop-blur-xs font-mono text-[11px]">
                        {category}
                      </Badge>
                    </div>

                    <div className="absolute bottom-3 right-3 bg-primary text-primary-foreground p-2 rounded-lg shadow-sm">
                      <IconComponent className="size-4" />
                    </div>
                  </div>

                  <CardHeader className="pb-3 pt-5">
                    <CardTitle className="text-xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                      {title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-between gap-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {desc}
                    </CardDescription>

                    <div className="space-y-2 pt-2 border-t border-border/60">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Layers className="size-3.5 text-primary" />
                        {lang === "vi" ? "ĐIỂM NỔI BẬT" : "KEY HIGHLIGHTS"}
                      </p>
                      <ul className="space-y-1.5">
                        {features.map((feat, idx) => <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                            <CheckCircle2 className="size-3.5 text-secondary shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Tailored Center Notice Band & CTA */}
        <div className="mt-12 p-6 md:p-8 rounded-xl bg-muted/50 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="p-3 rounded-xl bg-primary/10 text-primary shrink-0">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">
                {lang === "vi" ? "Cam kết chất lượng thiết bị & An toàn tại mọi trung tâm" : "Quality & Safety Commitment Across All Locations"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                {lang === "vi" ? "Mặc dù cơ sở vật chất được bố trí tinh chỉnh riêng cho từng mặt bằng cơ sở, ROBOTO cam kết 100% học viên đều tiếp cận tiêu chuẩn giáo cụ LEGO Education chính hãng và giáo trình đồng bộ." : "While facilities are tailored specifically for each campus footprint, ROBOTO guarantees 100% of students access authentic LEGO Education kits and unified curriculum standards."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto font-semibold">
              <Link to="/FacilitiesCentersPage">
                {lang === "vi" ? "Xem Danh Sách Trung Tâm" : "View All Centers"}
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full md:w-auto">
              <Link to="/FreeTrialContactPage">
                {lang === "vi" ? "Đăng Ký Tham Quan" : "Book a Tour"}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}